import React, { Suspense, useState } from "react";
import { motion } from "framer-motion";
import { Ship, Plane, Truck, Container, ArrowUpRight } from "lucide-react";
import Hero3D from "../three/Hero3D";
import TransportViewer from "../three/TransportViewer";

const MODES = [
  { id: "ship", label: "Sea", Icon: Ship, testId: "3d-mode-switch-ship" },
  { id: "plane", label: "Air", Icon: Plane, testId: "3d-mode-switch-plane" },
  { id: "truck", label: "Road", Icon: Truck, testId: "3d-mode-switch-truck" },
  { id: "container", label: "Cargo", Icon: Container, testId: "3d-mode-switch-container" },
];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 1, delay: 0.3 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

const TITLE = ["Connecting India,", "Delivering", "the World."];

export default function Hero({ onQuote }) {
  const [mode, setMode] = useState("ship");

  return (
    <section id="top" className="relative min-h-screen overflow-hidden grain">
      <div className="absolute inset-0 z-0">
        <Suspense fallback={null}>
          <Hero3D />
        </Suspense>
      </div>
      <div className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-r from-[#0B1320] via-[#0B1320]/45 via-50% to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-32 bg-gradient-to-t from-[#0B1320] to-transparent" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-5 pt-28 pb-16 sm:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[#E65100]" />
          <span className="data-label" data-testid="hero-eyebrow">Global Export • Import • On-Demand Trade</span>
        </motion.div>

        <h1 data-testid="hero-title" className="font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-7xl">
          {TITLE.map((t, i) => (
            <span key={i} className="mask-line">
              <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
                {i === 1 ? <span className="text-gradient">{t}</span> : t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-7 max-w-xl text-base text-slate-300 sm:text-lg"
        >
          Suryavansh Exim moves raw materials, agricultural goods and on-demand cargo across 42 countries — by ship, air and road, with customs mastery at every border.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={onQuote}
            data-testid="hero-cta-quote"
            className="group flex items-center gap-2 rounded-full bg-[#E65100] px-7 py-3.5 font-semibold text-white transition-all duration-300 hover:bg-[#FF6D00] hover:shadow-[0_0_40px_rgba(230,81,0,0.55)]"
          >
            Request a Quote
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
          <a href="#services" data-testid="hero-cta-services" className="rounded-full border border-white/15 px-7 py-3.5 font-semibold text-white transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]">
            Explore Services
          </a>
        </motion.div>
      </div>

      {/* Transport mode viewer */}
      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.5, duration: 0.9 }}
        className="absolute bottom-6 right-6 z-10 hidden w-[460px] xl:block"
      >
        <div className="glass rounded-2xl p-5 shadow-[0_25px_60px_rgba(0,0,0,0.8)] border border-white/20">
          <div className="h-64 w-full bg-[#0B1320]/75 rounded-xl overflow-hidden border border-white/10 relative shadow-inner">
            <Suspense fallback={null}>
              <TransportViewer mode={mode} />
            </Suspense>
          </div>
          <div className="mt-3 grid grid-cols-4 gap-2">
            {MODES.map(({ id, label, Icon, testId }) => (
              <button
                key={id}
                data-testid={testId}
                onClick={() => setMode(id)}
                className={`flex flex-col items-center gap-1 rounded-lg py-2 text-xs transition-all duration-300 ${mode === id ? "bg-[#E65100] text-white" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
